import { env } from '@/config/environment';

export interface MeetingAttendee {
  userId: string;
  profilePhotoUrl: string | null;
}

export interface MeetingStats {
  totalParticipantCount: number;
  recentAttendees: MeetingAttendee[];
}

export interface BatchMeetingStats {
  [meetingId: string]: MeetingStats;
}

class MeetingStatsService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = env.apiUrl;
  }

  async getMeetingStats(meetingId: string): Promise<MeetingStats | null> {
    try {
      const response = await fetch(`${this.baseUrl}/api/meetings/stats/${meetingId}`);
      const data = await response.json();

      if (data.success) {
        return {
          totalParticipantCount: data.totalParticipantCount,
          recentAttendees: data.recentAttendees
        };
      }

      console.error('Failed to fetch meeting stats:', data.error);
      return null;
    } catch (error) {
      console.error('Error fetching meeting stats:', error);
      return null;
    }
  }

  private async fetchChunk(meetingIds: string[]): Promise<BatchMeetingStats> {
    try {
      const response = await fetch(`${this.baseUrl}/api/meetings/stats/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ meetingIds })
      });
      
      const data = await response.json();

      if (data.success) {
        return data.stats;
      }

      console.warn('Failed to fetch chunk meeting stats:', data.error);
      return {};
    } catch (error) {
      console.warn('Error fetching chunk meeting stats:', error);
      return {};
    }
  }

  async getBatchMeetingStats(meetingIds: string[]): Promise<BatchMeetingStats> {
    if (meetingIds.length === 0) {
      return {};
    }

    const CHUNK_SIZE = 50;
    const MAX_CONCURRENT = 3;

    // Split into chunks of 50
    const chunks: string[][] = [];
    for (let i = 0; i < meetingIds.length; i += CHUNK_SIZE) {
      chunks.push(meetingIds.slice(i, i + CHUNK_SIZE));
    }

    // If only one chunk, fetch directly
    if (chunks.length === 1 && chunks[0]) {
      return this.fetchChunk(chunks[0]);
    }

    // Fetch chunks with limited concurrency
    const results: BatchMeetingStats = {};
    
    for (let i = 0; i < chunks.length; i += MAX_CONCURRENT) {
      const batch = chunks.slice(i, i + MAX_CONCURRENT);
      const batchResults = await Promise.all(batch.map(chunk => this.fetchChunk(chunk)));
      
      // Merge results
      for (const chunkResult of batchResults) {
        Object.assign(results, chunkResult);
      }
    }

    return results;
  }
}

export const meetingStatsService = new MeetingStatsService();
export default meetingStatsService;
